/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// AttendedChecklistV2
//
// Renders the Attended Checklist surface on the lead-detail page using V2
// components (ChecklistItemRow). Replaces the V1 ChecklistTab inline + tab
// surfaces in OpenAttendedPopup / OpenConsultationPopup / OverView.
//
// Visibility gate (managed by parent via the `onVisibilityChange` callback or
// the exported `useAttendedChecklistVisibility` hook):
//   - The lead has at least one lead_treatment in CONSULTATION_ATTENDED, AND
//   - There's a V2 task whose template applies_to_statuses includes
//     CONSULTATION_ATTENDED AND that task has checklist cells.
//
// Data shape — uses two existing endpoints (no new BE):
//   1. /api/v1/practices/{p}/leads/{l}/task-checklists/  → grouped by template
//   2. /api/v1/practices/{p}/task-templates/?limit=200    → for applies_to_statuses
//
// We can't filter by `applies_to_status` server-side (no such param exists), so
// we fetch all groups and filter client-side using the templates lookup.
// Tasks within each surfaced group are rendered with title + ChecklistItemRow
// for each checklist cell, sorted by due_at.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'src/redux/store';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    Typography,
    LinearProgress,
    Stack,
    Skeleton,
    Alert,
    Button,
    Chip,
    Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { useAuth } from 'src/hooks';
import { primary, info } from 'src/theme/palette';
import ChecklistItemRow from 'src/sections/tasks/ChecklistItemRow';
import { filterGroupsByTreatment } from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';
import { parseApiDate } from 'src/utils/taskDateUtils';

const ATTENDED_STATUS = 'CONSULTATION_ATTENDED';

// Hook: parent uses this to decide whether to render the surface at all.
// Returns { ready, visible, leadInAttended, hasAttendedTask }.
export function useAttendedChecklistVisibility({ practiceId, leadId, leadDetail }) {
    const [state, setState] = useState({
        ready: false,
        visible: false,
        leadInAttended: false,
        hasAttendedTask: false,
        debug: null, // populated for superadmin-only diagnostic tab
    });
    // #215 (I-NEW-2) — React to template edits. The taskTemplate slice bumps
    // `templatesUpdatedAt` on every mutation (create/update/delete/archive/
    // clone/revert). Subscribing here re-runs the visibility check so an
    // admin's template edit immediately shows/hides the checklist surface
    // without a full page reload.
    const templatesUpdatedAt = useSelector(
        (s) => s?.taskTemplate?.templatesUpdatedAt || 0
    );

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!practiceId || !leadId) {
                setState({ ready: true, visible: false, leadInAttended: false, hasAttendedTask: false });
                return;
            }
            const treatments = leadDetail?.lead_treatments || [];
            const leadInAttended = treatments.some((t) => t.status === ATTENDED_STATUS);

            if (!leadInAttended) {
                if (!cancelled) {
                    setState({ ready: true, visible: false, leadInAttended: false, hasAttendedTask: false });
                }
                return;
            }
            if (!hasFeatureAccess('tasks-v2', practiceId)) {
                if (!cancelled) {
                    setState({ ready: true, visible: false, leadInAttended, hasAttendedTask: false, debug: { featureAccess: false } });
                }
                return;
            }
            try {
                const [groupsRes, tplRes] = await Promise.all([
                    callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`),
                    callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
                ]);
                let groups = [];
                if (Array.isArray(groupsRes?.data)) groups = groupsRes.data;
                else if (Array.isArray(groupsRes?.data?.results)) groups = groupsRes.data.results;
                let tplList = [];
                if (Array.isArray(tplRes?.data?.results)) tplList = tplRes.data.results;
                else if (Array.isArray(tplRes?.data)) tplList = tplRes.data;
                const tplStatuses = new Map();
                const tplTreatments = new Map();
                tplList.forEach((t) => {
                    tplStatuses.set(String(t.id), Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []);
                    tplTreatments.set(String(t.id), Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []);
                });
                // Build one scope per treatment — same strategy as useGuidedChecklist.
                // For each treatment, filterGroupsByTreatment filters to groups whose
                // template matches that treatment's item id AND whose tasks belong to
                // that specific lead_treatment row (preventing cross-treatment bleed).
                // Union the results across all treatments and deduplicate generic groups
                // that appear in every scope (keyed by template_id::sorted-task-ids).
                const allTreatments = leadDetail?.lead_treatments || [];
                const fallbackTreatment =
                    allTreatments.find((t) => t.status === ATTENDED_STATUS) ||
                    allTreatments.find((t) => t.status === 'CONSULTATION_BOOKED') ||
                    allTreatments[0];
                const treatmentScopes = allTreatments.length > 0
                    ? allTreatments.map((tx) => ({
                        treatment: tx,
                        leadTreatmentId: tx.treatment_data?.treatment_item?.id ?? null,
                        matchedLeadTreatmentId: tx.id ?? null,
                        label: tx.treatment_data?.name || tx.treatment_data?.treatment_item?.name || null,
                    }))
                    : [{
                        treatment: fallbackTreatment ?? null,
                        leadTreatmentId: fallbackTreatment?.treatment_data?.treatment_item?.id ?? null,
                        matchedLeadTreatmentId: fallbackTreatment?.id ?? null,
                        label: null,
                    }];
                const multiTxVisibility = treatmentScopes.length > 1;
                const rawMatchingGroups = treatmentScopes.flatMap(({ leadTreatmentId, matchedLeadTreatmentId, label }) => {
                    const scopeGroups = filterGroupsByTreatment(groups, {
                        tplStatusMap: tplStatuses,
                        tplTreatmentMap: tplTreatments,
                        leadTreatmentId,
                        matchedLeadTreatmentId,
                    });
                    if (!multiTxVisibility || !label) return scopeGroups;
                    // Only treatment-specific groups get the label; generic groups
                    // (no applies_to_treatment_items) pass every scope and must not
                    // inherit the first scope's treatment name.
                    return scopeGroups.map((g) => {
                        const isTreatmentSpecific = (tplTreatments.get(String(g.template_id)) || []).length > 0;
                        return { ...g, _treatmentLabel: isTreatmentSpecific ? label : undefined };
                    });
                });
                const seenVisKeys = new Set();
                const matchingGroups = rawMatchingGroups.filter((g) => {
                    const taskIds = (g.tasks || []).map((t) => t.id).sort().join(',');
                    const key = `${g.template_id}::${taskIds}`;
                    if (seenVisKeys.has(key)) return false;
                    seenVisKeys.add(key);
                    return true;
                });
                const attendedTemplates = tplList.filter((t) => (Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []).includes(ATTENDED_STATUS));
                const groupsWithItems = matchingGroups.filter((g) => (g.tasks || []).some((task) => (task.checklist_items || []).length > 0));
                const hasAttendedTask = groupsWithItems.length > 0;
                const debug = {
                    featureAccess: true,
                    groupsCount: groups.length,
                    templatesCount: tplList.length,
                    attendedTemplatesCount: attendedTemplates.length,
                    matchingGroupsCount: matchingGroups.length,
                    groupsWithItemsCount: groupsWithItems.length,
                    sampleTask: matchingGroups[0]?.tasks?.[0] ? {
                        id: matchingGroups[0].tasks[0].id,
                        title: matchingGroups[0].tasks[0].title,
                        keys: Object.keys(matchingGroups[0].tasks[0]),
                        checklist_items_len: (matchingGroups[0].tasks[0].checklist_items || []).length,
                    } : null,
                };
                if (!cancelled) {
                    setState({ ready: true, visible: hasAttendedTask, leadInAttended, hasAttendedTask, debug });
                }
            } catch (err) {
                Sentry.captureException(err, { tags: { context: 'attended_checklist_v2_visibility' } });
                if (!cancelled) {
                    setState({ ready: true, visible: false, leadInAttended, hasAttendedTask: false, debug: { error: err?.message || 'fetch failed' } });
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [practiceId, leadId, JSON.stringify((leadDetail?.lead_treatments || []).map((t) => ({ id: t.id, s: t.status, ti: t.treatment_data?.treatment_item?.id ?? null }))), templatesUpdatedAt]);

    return state;
}

// -----------------------------------------------------------------------------

export default function AttendedChecklistV2({
    practiceId,
    leadId,
    leadDetail,
    compact,
    onVisibilityChange,
}) {
    const { user } = useAuth();
    const isSuperuser = !!user?.is_superuser;

    const [groups, setGroups] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    const treatments = leadDetail?.lead_treatments || [];
    const leadInAttended = treatments.some((t) => t.status === ATTENDED_STATUS);

    const fetchAll = useCallback(async () => {
        if (!practiceId || !leadId) return;
        // Match the BE gate on `tasks-v2` so users without the feature don't
        // generate 403s on /task-checklists/ + /task-templates/ each time a
        // lead-detail page mounts. practiceId-aware to close the
        // practice-switch window leak.
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setGroups([]);
            setTemplates([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setFetchError(null);
        try {
            const [groupsRes, tplRes] = await Promise.all([
                callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`),
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
            ]);
            let groupsData = [];
            if (Array.isArray(groupsRes?.data)) groupsData = groupsRes.data;
            else if (Array.isArray(groupsRes?.data?.results)) groupsData = groupsRes.data.results;
            let tplData = [];
            if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
            else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;
            setGroups(groupsData);
            setTemplates(tplData);
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'attended_checklist_v2_fetch' } });
            setFetchError('Failed to load attended checklist.');
        } finally {
            setLoading(false);
        }
    }, [practiceId, leadId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const tplStatusMap = useMemo(() => {
        const m = new Map();
        templates.forEach((t) => m.set(String(t.id), Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []));
        return m;
    }, [templates]);

    const tplTreatmentsMap = useMemo(() => {
        const m = new Map();
        templates.forEach((t) => m.set(String(t.id), Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []));
        return m;
    }, [templates]);

    // Build treatment scopes — one per lead_treatment — and union the filtered
    // groups across all scopes. This mirrors the useGuidedChecklist approach so
    // multi-treatment leads (e.g. Invisalign + Dental Implants) show ALL their
    // attended checklist groups, not just the first ATTENDED treatment.
    // Generic groups (template with no treatment restriction) are deduplicated so
    // they appear only once even though they pass every scope's filter.
    const treatmentScopesForRender = useMemo(() => {
        const ts = leadDetail?.lead_treatments || [];
        if (ts.length === 0) {
            // Fallback: no treatments at all — use null scope (generic groups only)
            return [{ leadTreatmentId: null, matchedLeadTreatmentId: null, label: null }];
        }
        return ts.map((tx) => ({
            leadTreatmentId: tx.treatment_data?.treatment_item?.id ?? null,
            matchedLeadTreatmentId: tx.id ?? null,
            label: tx.treatment_data?.name || tx.treatment_data?.treatment_item?.name || null,
        }));
    }, [leadDetail]);

    // For the debug overlay we still surface the first attended treatment's id.
    const leadTreatmentId = useMemo(() => {
        const ts = leadDetail?.lead_treatments || [];
        const matched = ts.find((t) => t.status === ATTENDED_STATUS) ||
            ts.find((t) => t.status === 'CONSULTATION_BOOKED') ||
            ts[0];
        return matched?.treatment_data?.treatment_item?.id ?? null;
    }, [leadDetail]);

    // Filter groups per treatment scope, union, dedup, then filter to those with
    // checklist cells. Stamp _treatmentLabel on groups when >1 treatment present.
    const attendedGroups = useMemo(() => {
        const multiTx = treatmentScopesForRender.length > 1;
        const rawGroups = treatmentScopesForRender.flatMap(({ leadTreatmentId: ltId, matchedLeadTreatmentId, label }) => {
            const scopeGroups = filterGroupsByTreatment(groups, {
                tplStatusMap,
                tplTreatmentMap: tplTreatmentsMap,
                leadTreatmentId: ltId,
                matchedLeadTreatmentId,
            });
            if (!multiTx || !label) return scopeGroups;
            // Only treatment-specific groups get the label; generic groups
            // (no applies_to_treatment_items) pass every scope and must not
            // inherit the first scope's treatment name.
            return scopeGroups.map((g) => {
                const isTreatmentSpecific = (tplTreatmentsMap.get(String(g.template_id)) || []).length > 0;
                return { ...g, _treatmentLabel: isTreatmentSpecific ? label : undefined };
            });
        });
        // Deduplicate generic groups that pass every scope
        const seen = new Set();
        const deduped = rawGroups.filter((g) => {
            const taskIds = (g.tasks || []).map((t) => t.id).sort().join(',');
            const key = `${g.template_id}::${taskIds}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        return deduped
            .map((g) => {
                const tasksWithCells = (g.tasks || []).filter(
                    (task) => (task.checklist_items || []).length > 0
                );
                if (tasksWithCells.length === 0) return null;
                // Sort tasks by due_at (ascending; nulls last)
                const sorted = [...tasksWithCells].sort((a, b) => {
                    if (!a.due_at && !b.due_at) return 0;
                    if (!a.due_at) return 1;
                    if (!b.due_at) return -1;
                    return new Date(parseApiDate(a.due_at)) - new Date(parseApiDate(b.due_at));
                });
                return { ...g, tasks: sorted };
            })
            .filter(Boolean);
    }, [groups, tplStatusMap, tplTreatmentsMap, treatmentScopesForRender]);

    const visible = leadInAttended && attendedGroups.length > 0;

    useEffect(() => {
        if (loading) return;
        if (onVisibilityChange) onVisibilityChange(visible);
    }, [visible, loading, onVisibilityChange]);

    const cardSx = {
        p: compact ? 1.5 : 2,
        mb: compact ? 0 : 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
    };
    const headingSx = {
        fontWeight: 700,
        fontSize: compact ? '13px' : '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'text.secondary',
        mb: 1.5,
    };

    if (loading) {
        return (
            <Card sx={cardSx}>
                <Typography sx={headingSx}>Attended Checklist</Typography>
                <Stack spacing={1}>
                    {[1, 2].map((i) => (
                        <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 1.5 }} />
                    ))}
                </Stack>
            </Card>
        );
    }

    if (fetchError) {
        return (
            <Card sx={cardSx}>
                <Typography sx={headingSx}>Attended Checklist</Typography>
                <Alert severity="warning" action={<Button size="small" onClick={fetchAll}>Retry</Button>}>
                    {fetchError}
                </Alert>
            </Card>
        );
    }

    // Debug info — computed for superusers regardless of visibility.
    const debugInfo = isSuperuser ? {
        practiceId,
        leadId,
        featureAccess: hasFeatureAccess('tasks-v2', practiceId),
        leadInAttended,
        leadTreatmentId,
        leadTreatmentName: (leadDetail?.lead_treatments || [])
            .find((t) => t.status === ATTENDED_STATUS)?.treatment_data?.name || null,
        groupsFromApi: groups.length,
        templatesFromApi: templates.length,
        attendedTemplates: templates.filter((t) =>
            (Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []).includes(ATTENDED_STATUS)
        ).length,
        templatesMatchingTreatment: templates.filter((t) => {
            const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
            if (!statuses.includes(ATTENDED_STATUS)) return false;
            const tItems = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
            if (tItems.length === 0) return false; // generic — counted separately
            return leadTreatmentId != null && tItems.some((ti) => String(ti?.id) === String(leadTreatmentId));
        }).length,
        genericTemplates: templates.filter((t) => {
            const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
            return statuses.includes(ATTENDED_STATUS) &&
                (Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []).length === 0;
        }).length,
        attendedGroupsCount: attendedGroups.length,
        visible,
    } : null;

    const debugOverlay = isSuperuser ? (
        <Accordion
            disableGutters
            elevation={0}
            data-testid="attended-checklist-v2-debug"
            sx={{
                mt: 1,
                bgcolor: '#FAFAFA',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: '#6e6e73' }}>
                    🔧 V2 Visibility Debug (superadmin only)
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Box
                    component="pre"
                    sx={{
                        fontSize: 11,
                        fontFamily: 'monospace',
                        color: '#6e6e73',
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                    }}
                >
                    {JSON.stringify(debugInfo, null, 2)}
                </Box>
            </AccordionDetails>
        </Accordion>
    ) : null;

    // Visibility gate — render nothing (except debug overlay for superusers).
    if (!visible) return debugOverlay;

    return (
        <>
        <Card sx={cardSx}>
            <Typography sx={headingSx}>Attended Checklist</Typography>
            <Stack spacing={1.5}>
                {attendedGroups.map((group) => {
                    const { completed = 0, total = 0 } = group.progress || {};
                    const pct = total > 0 ? (completed / total) * 100 : 0;
                    return (
                        <Box
                            key={`${group.template_id}-${group.applied_at || ''}`}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                p: 1.25,
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                        {group.template_name}
                                    </Typography>
                                    {/* Treatment label pill — only present on multi-treatment leads.
                                        Absent (undefined) for single-treatment leads: no chip, no layout shift. */}
                                    {group._treatmentLabel && (
                                        <Box
                                            data-testid="attended-checklist-treatment-label"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: 0.875,
                                                py: 0.125,
                                                borderRadius: '980px',
                                                bgcolor: alpha(info.main, 0.10),
                                                color: info.dark,
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                flexShrink: 0,
                                                maxWidth: 130,
                                            }}
                                        >
                                            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {group._treatmentLabel}
                                            </Box>
                                        </Box>
                                    )}
                                </Stack>
                                <Chip
                                    label={`${completed} / ${total}`}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: alpha(primary.main, 0.1),
                                        color: pct === 100 ? 'success.main' : primary.main,
                                        fontWeight: 600,
                                        flexShrink: 0,
                                    }}
                                />
                            </Stack>
                            {total > 0 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    sx={{
                                        height: 5,
                                        borderRadius: 3,
                                        mb: 1,
                                        bgcolor: 'rgba(0,0,0,0.08)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 3,
                                            bgcolor: pct === 100 ? '#00AB55' : primary.main,
                                        },
                                    }}
                                />
                            )}
                            <Stack spacing={1.25} sx={{ mt: 1 }}>
                                {group.tasks.map((task, taskIdx) => (
                                    <Box key={task.id}>
                                        {taskIdx > 0 && <Divider sx={{ my: 1 }} />}
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem', color: 'text.primary' }}>
                                                {task.title}
                                            </Typography>
                                            {task.due_at && (
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                                                    · due {new Date(parseApiDate(task.due_at)).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Stack spacing={0.5}>
                                            {(task.checklist_items || []).map((cell) => (
                                                <ChecklistItemRow
                                                    key={cell.id || cell.checklist_item_id}
                                                    item={cell}
                                                    practiceId={practiceId}
                                                    taskId={task.id}
                                                    leadData={leadDetail || { id: leadId }}
                                                    onDone={fetchAll}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </Card>
        {debugOverlay}
        </>
    );
}

AttendedChecklistV2.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadDetail: PropTypes.object,
    compact: PropTypes.bool,
    onVisibilityChange: PropTypes.func,
};

AttendedChecklistV2.defaultProps = {
    leadDetail: null,
    compact: false,
    onVisibilityChange: null,
};
